/**
 * @flow
 */

import React, { useCallback, useState } from 'react';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { Modal, Header, Button, Icon, Message } from 'semantic-ui-react';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import styled from 'styled-components';
import type { ModalDialog, Product } from '../../../type';

import { GET_PRODUCTS_QUERY } from '../containers/ProductsList';
import { get } from 'lodash';

const intlMessages = defineMessages({
    existingsError: {
        id: 'pages.inventory.form.ProductForm.existingsError',
        defaultMessage: 'This product does not exists',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

const DELETE_PRODUCT_QUERY = gql`
    mutation deleteProduct($input: DeleteProductInput!) {
        deleteProduct(input: $input) {
            productId
        }
    }
`;

const MessageBlock = styled(Message)`
    background-color: transparent !important;
    border: 2px solid #ff4545;
    color: #ff4545 !important;
    font-weight: bold;
    box-shadow: none !important;
`;

type DeleteProductDialogProps = {
    product: Product,
    intl: Object,
} & ModalDialog;

const DeleteProductDialog = ({
    product,
    trigger,
    open,
    onClose,
    intl,
}: DeleteProductDialogProps) => {
    const salon = useSelector(({ user }) => user.get('salon'));
    const searchQuery = useSelector(({ inventory }) => inventory.get('searchQuery'));
    const manufacturersFilter = useSelector(({ inventory }) => inventory.get('manufacturersFilter'));

    const [deleteProduct, { loading }] = useMutation(DELETE_PRODUCT_QUERY);
    const [error, setError] = useState(null);

    const onDelete = useCallback(() => {
        setError(null);
        // No optimisticResponse: the row stays visible while the request is
        // in flight so the modal can show an error if the server rejects it.
        // On success the cache update removes the row, unmounting this
        // component naturally — no need to call onClose() in .then().
        deleteProduct({
            variables: {
                input: { productId: product.id },
            },
            update: (store, { data: { deleteProduct: result } }) => {
                const data = store.readQuery({
                    query: GET_PRODUCTS_QUERY,
                    variables: {
                        salon,
                        searchQuery,
                        manufacturersFilter,
                        first: 500,
                    },
                });

                if (data && data.viewer && data.viewer.salon && data.viewer.salon.products) {
                    store.writeQuery({
                        query: GET_PRODUCTS_QUERY,
                        variables: {
                            salon,
                            searchQuery,
                            manufacturersFilter,
                            first: 500,
                        },
                        data: {
                            ...data,
                            viewer: {
                                ...data.viewer,
                                salon: {
                                    ...data.viewer.salon,
                                    products: {
                                        ...data.viewer.salon.products,
                                        edges: data.viewer.salon.products.edges.filter(
                                            edge => edge.node.id !== product.id,
                                        ),
                                    },
                                },
                            },
                        },
                    });
                }
            },
        }).catch(err => {
            const graphQLError = err.graphQLErrors && err.graphQLErrors[0];
            if (graphQLError && graphQLError.name === 'NotExists' && get(graphQLError, 'data.error') === 'ID_DOES_NOT_EXISTS') {
                setError(intl.formatMessage(intlMessages.existingsError));
            } else {
                setError(intl.formatMessage(intlMessages.defaultErrorMessage));
            }
        });
    }, [deleteProduct, salon, searchQuery, manufacturersFilter, product, intl]);

    return (
        <Modal trigger={trigger} open={open} onClose={onClose} size="small" basic>
            <Header icon>
                <Icon name="remove" />
                <FormattedMessage
                    id="pages.inventory.DeleteProductDialog.deleteProduct"
                    defaultMessage="Delete Product"
                />
            </Header>
            <Modal.Content>
                <FormattedMessage
                    id="pages.inventory.DeleteProductDialog.confirmMessage"
                    defaultMessage="Are you sure you want to delete {product} from {manufacturer}?"
                    values={{
                        product: product.name,
                        manufacturer: product.manufacturer.name,
                    }}
                >
                    {message => <p>{message}</p>}
                </FormattedMessage>
                {error && (
                    <MessageBlock error>
                        <Icon name="warning sign" />
                        {error}
                    </MessageBlock>
                )}
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose} color="red" basic inverted disabled={loading}>
                    <Icon name="remove" />
                    <FormattedMessage
                        id="pages.inventory.DeleteProductDialog.no"
                        defaultMessage="No"
                    />
                </Button>
                <Button onClick={onDelete} color="green" inverted loading={loading} disabled={loading}>
                    <Icon name="checkmark" />
                    <FormattedMessage
                        id="pages.inventory.DeleteProductDialog.yes"
                        defaultMessage="Yes"
                    />
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default injectIntl(DeleteProductDialog);
